import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, CalendarDays, CalendarHeart, Upload, LogOut, ShieldCheck, Eye, Megaphone, HandCoins, BarChart3, TrendingUp, UserCog, Network, DatabaseBackup, Settings, ChevronDown, Shield, Palette, Briefcase, GraduationCap, GripVertical, ArrowDownAZ, RotateCcw, PanelLeftClose, PanelLeftOpen, Lock } from "lucide-react";
import { useBranding } from "@/lib/branding";
import { useUiState } from "@/lib/uiState";
import { api } from "@/lib/api";
import Footer from "@/components/Footer";

const UD_SEAL = "https://customer-assets-wrfwihn1.emergentagent.net/job_alumni-hub-101/artifacts/n52mfin2_image.png";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/alumni", label: "Directory", icon: Users, testid: "nav-directory" },
  { to: "/classes", label: "Classes", icon: CalendarDays, testid: "nav-classes" },
  { to: "/events", label: "Events", icon: CalendarHeart, testid: "nav-events" },
  { to: "/communications", label: "Communications", icon: Megaphone, testid: "nav-communications" },
  { to: "/class-agents", label: "Class Agents", icon: UserCog, testid: "nav-class-agents", adminOnly: true },
  { to: "/groups", label: "Chapters & Groups", icon: Network, testid: "nav-groups", adminOnly: true },
  { to: "/fundraising", label: "Fundraising", icon: HandCoins, testid: "nav-fundraising" },
  { to: "/careers", label: "Career Network", icon: Briefcase, testid: "nav-careers" },
  { to: "/faculty", label: "Faculty & Staff", icon: GraduationCap, testid: "nav-faculty" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/engagement", label: "Engagement", icon: TrendingUp, testid: "nav-engagement" },
  { to: "/import", label: "Import", icon: Upload, testid: "nav-import", adminOnly: true },
  { to: "/backup", label: "Backup & Restore", icon: DatabaseBackup, testid: "nav-backup", adminOnly: true },
  { to: "/users", label: "Users & Admins", icon: Shield, testid: "nav-users", superOnly: true },
  { to: "/branding", label: "Branding", icon: Palette, testid: "nav-branding", superOnly: true },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin, isSuperadmin, stopImpersonation } = useAuth();
  const isManager = user?.role === "alumni_manager";
  const RESTRICTED = new Set(["/users", "/branding", "/backup", "/reports"]);
  const b = useBranding();
  const loc = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const exitEmulation = async () => {
    await stopImpersonation();
    const back = localStorage.getItem("ud_emulate_return");
    localStorage.removeItem("ud_emulate_return");
    navigate(back || "/users");
  };
  const [alpha, setAlpha] = useUiState("ud_nav_alpha", false);
  const [order, setOrder] = useUiState("ud_nav_order", []);
  const [reorder, setReorder] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [collapsed, setCollapsed] = useUiState("ud_nav_collapsed", false);
  const hydrated = useRef(false);
  const active = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  // Hydrate nav prefs from the account (follows the admin across devices).
  useEffect(() => {
    let cancelled = false;
    api.get("/me/ui-prefs").then((r) => {
      if (cancelled) return;
      const p = r.data || {};
      if (Array.isArray(p.nav_order)) setOrder(p.nav_order);
      if (typeof p.nav_alpha === "boolean") setAlpha(p.nav_alpha);
      if (typeof p.sidebar_collapsed === "boolean") setCollapsed(p.sidebar_collapsed);
      hydrated.current = true;
    }).catch(() => { hydrated.current = true; });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  // Persist prefs to the account whenever they change (after hydration).
  useEffect(() => {
    if (!hydrated.current) return;
    api.put("/me/ui-prefs", { nav_order: order, nav_alpha: alpha, sidebar_collapsed: collapsed }).catch(() => {});
  }, [order, alpha, collapsed]);

  const visibleNav = nav.filter((n) => (isManager && RESTRICTED.has(n.to)) || ((!n.adminOnly || isAdmin) && (!n.superOnly || isSuperadmin)));
  const byTo = Object.fromEntries(visibleNav.map((n) => [n.to, n]));
  let items;
  if (alpha) {
    items = [...visibleNav].sort((a, b) => a.label.localeCompare(b.label));
  } else if (order && order.length) {
    const ordered = order.map((t) => byTo[t]).filter(Boolean);
    const missing = visibleNav.filter((n) => !order.includes(n.to));
    items = [...ordered, ...missing];
  } else {
    items = visibleNav;
  }
  const moveItem = (from, to) => {
    const seq = items.map((n) => n.to);
    const [m] = seq.splice(from, 1);
    seq.splice(to, 0, m);
    setOrder(seq);
  };
  const enterReorder = () => { setAlpha(false); setReorder(true); };

  return (
    <div className="min-h-screen flex bg-background">
      <aside className={`${collapsed ? "w-16" : "w-64"} shrink-0 bg-secondary text-white flex-col hidden md:flex sticky top-0 h-screen transition-[width] duration-200`}>
        <div className={`px-4 py-6 border-b border-white/10 flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-6"}`}>
          <a href={b.university_url} target="_blank" rel="noopener noreferrer" title={`Visit ${b.university_name}`} data-testid="ud-seal-link" className="h-11 w-11 rounded-full bg-white flex items-center justify-center p-0.5 shrink-0 overflow-hidden transition-transform hover:scale-105">
            {b.logo_primary ? <img src={b.logo_primary} alt="seal" className="h-full w-full object-contain" /> : <span className="font-serif text-secondary">{(b.university_name || "U").slice(0, 1)}</span>}
          </a>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="font-serif text-lg leading-none tracking-tight truncate">{b.university_name}</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-accent mt-1">{b.short_name}</div>
            </div>
          )}
        </div>
        <div className={`px-3 pt-2 ${collapsed ? "flex justify-center" : ""}`}>
          <button data-testid="nav-collapse-toggle" onClick={() => { setCollapsed((c) => !c); setReorder(false); }} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`flex items-center gap-1.5 rounded text-[11px] font-medium text-white/60 hover:bg-white/10 hover:text-white transition-colors ${collapsed ? "p-2" : "px-2 py-1 w-full"}`}>
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4" /> Collapse</>}
          </button>
        </div>
        <nav className="flex-1 py-3 px-3 space-y-1 overflow-y-auto">
          {!collapsed && (
          <div className="flex items-center gap-1 px-1 pb-2 mb-1 border-b border-white/10" data-testid="nav-controls">
            <button data-testid="nav-alpha-toggle" onClick={() => { setAlpha((a) => !a); setReorder(false); }} title="Sort A–Z"
              className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors ${alpha ? "bg-accent text-accent-foreground" : "text-white/60 hover:bg-white/10 hover:text-white"}`}>
              <ArrowDownAZ className="h-3.5 w-3.5" /> A–Z
            </button>
            <button data-testid="nav-reorder-toggle" onClick={() => (reorder ? setReorder(false) : enterReorder())} title="Drag to reorder"
              className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors ${reorder ? "bg-accent text-accent-foreground" : "text-white/60 hover:bg-white/10 hover:text-white"}`}>
              <GripVertical className="h-3.5 w-3.5" /> {reorder ? "Done" : "Reorder"}
            </button>
            {(order?.length > 0 || alpha) && (
              <button data-testid="nav-reset-order" onClick={() => { setOrder([]); setAlpha(false); }} title="Reset to default order"
                className="ml-auto flex items-center gap-1 px-2 py-1 rounded text-[11px] text-white/50 hover:bg-white/10 hover:text-white transition-colors">
                <RotateCcw className="h-3 w-3" /> Reset
              </button>
            )}
          </div>
          )}
          {items.map((n, i) => {
            const Icon = n.icon;
            const locked = isManager && RESTRICTED.has(n.to);
            if (locked) {
              return (
                <div key={n.to} data-testid={`nav-locked-${n.testid}`} title="Not available for your role"
                  className={`flex items-center gap-3 rounded-md text-sm font-medium text-white/25 cursor-not-allowed select-none ${collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"}`}>
                  <Icon className="h-4 w-4 shrink-0" /> {!collapsed && <><span className="flex-1">{n.label}</span><Lock className="h-3 w-3" /></>}
                </div>
              );
            }
            if (reorder && !collapsed) {
              return (
                <div
                  key={n.to}
                  data-testid={`nav-drag-${n.testid}`}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (dragIdx !== null && dragIdx !== i) moveItem(dragIdx, i); setDragIdx(null); }}
                  onDragEnd={() => setDragIdx(null)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium cursor-move select-none border border-dashed transition-colors ${dragIdx === i ? "border-accent bg-white/10" : "border-white/20 bg-white/5 text-white/80 hover:bg-white/10"}`}
                >
                  <GripVertical className="h-4 w-4 text-white/40 shrink-0" />
                  <Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{n.label}</span>
                </div>
              );
            }
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                title={collapsed ? n.label : undefined}
                className={`flex items-center gap-3 rounded-md text-sm font-medium transition-colors ${collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"} ${
                  active(n.to) ? "bg-accent text-accent-foreground font-semibold" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" /> {!collapsed && n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 relative">
          <button
            data-testid="user-menu-button"
            onClick={() => setMenuOpen((o) => !o)}
            title={collapsed ? user?.name : undefined}
            className={`w-full flex items-center gap-2 px-1 py-1 rounded-md hover:bg-white/5 transition-colors ${collapsed ? "justify-center" : ""}`}
          >
            <div className="h-8 w-8 rounded-full bg-accent text-secondary flex items-center justify-center text-xs font-bold shrink-0">
              {(user?.name || "U").slice(0, 1)}
            </div>
            {!collapsed && (<>
            <div className="min-w-0 text-left flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="flex items-center gap-1 text-[11px] text-white/50">
                {isManager ? <UserCog className="h-3 w-3" /> : isAdmin ? <ShieldCheck className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {isManager ? "Alumni Manager" : isAdmin ? "Administrator" : "Read Only"}
              </div>
            </div>
            <ChevronDown className={`h-4 w-4 text-white/50 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </>)}
          </button>
          {menuOpen && (
            <div data-testid="user-menu" className="absolute bottom-full left-4 right-4 mb-1 bg-card text-foreground rounded-md shadow-lg border border-border overflow-hidden">
              <Link to="/settings" data-testid="menu-settings" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-muted">
                <Settings className="h-4 w-4" /> Account settings
              </Link>
              <button data-testid="menu-logout" onClick={() => { setMenuOpen(false); logout(); navigate("/login"); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-muted text-destructive border-t border-border">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </aside>
      <main className="flex-1 min-w-0 relative bg-slate-300/60 min-h-screen">
        <div className="relative z-10">
          {user?.impersonated && (
            <div data-testid="admin-impersonation-banner" className="bg-accent text-secondary text-sm font-medium">
              <div className="max-w-7xl mx-auto px-5 sm:px-8 py-2 flex items-center justify-between gap-3">
                <span className="flex items-center gap-2"><Eye className="h-4 w-4" /> Emulating <b>{user?.name || user?.email}</b> ({user?.role}) — you are viewing the app as they see it</span>
                <button data-testid="admin-exit-preview" onClick={exitEmulation} className="flex items-center gap-1.5 px-3 py-1 rounded bg-secondary text-white text-xs font-medium hover:bg-secondary/90"><LogOut className="h-3.5 w-3.5" /> Exit emulation</button>
              </div>
            </div>
          )}
          <div className="md:hidden sticky top-0 z-20 bg-secondary text-white px-4 py-3 flex items-center justify-between">
            <span className="font-serif text-lg">{b.university_name}</span>
            <button data-testid="mobile-logout" onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="h-5 w-5" />
            </button>
          </div>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">{children}</div>
          <Footer />
        </div>
      </main>
    </div>
  );
}
